import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERFIS_CONTATO, type Contato, type EvidenciaOptin } from '@eleg360/types';
import { contatosApi } from '@/lib/api/contatos';
import { criarComFilaOffline } from '@/lib/hooks/useCriacaoOffline';
import { categoriasApi } from '@/lib/api/core';
import { liderancasApi } from '@/lib/api/liderancas';
import { useAuthStore } from '@/lib/stores/authStore';
import { useMockStore } from '@/lib/stores/mockStore';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { useBairros } from '@/lib/hooks/useBairros';
import { useEscala } from '@/lib/escala';
import { exportarCsv } from '@/lib/csv';
import { podeEnviarWhatsApp, motivoBloqueio } from '@/lib/lgpd';
import { CardConsentimento } from '@/modules/core/lgpd/CardConsentimento';
import { Anexos } from '../anexos/Anexos';
import { visitasApi } from '@/lib/api/visitas';
import {
  ROTULO_BASE_LEGAL,
  formatarData,
  formatarDiaMes,
  formatarTelefone,
  paraE164,
} from '@/lib/format';
import { Info } from '@/design-system/Info';

/**
 * As tags vinham fixas aqui e divergiram silenciosamente da lista prevista no
 * projeto -- cinco de dez coincidiam. Agora vêm de core.categoria (módulo
 * `contato`), a mesma tabela que já alimenta os seletores de solicitação, ação
 * e agenda, e que o gabinete edita na tela de Configurações.
 */

/**
 * Como o consentimento foi obtido. `base_legal` e `optin_ts` dizem que houve e
 * quando; a evidencia diz como -- e e o que sustenta a base legal numa
 * fiscalizacao.
 */
const EVIDENCIAS_OPTIN: Array<{ v: EvidenciaOptin; l: string }> = [
  { v: 'nao_informada', l: 'Não informada' },
  { v: 'formulario', l: 'Formulário assinado ou digital' },
  { v: 'whatsapp', l: 'Mensagem de WhatsApp' },
  { v: 'audio', l: 'Áudio ou gravação' },
  { v: 'presencial', l: 'Presencial, com testemunha' },
  { v: 'importacao', l: 'Importação (prova sob guarda do gabinete)' },
];

const PAGE_SIZE = 12;

type FormContato = {
  nome: string;
  telefone: string;
  email: string;
  bairro: string;
  nascimento: string;
  perfil: string[];
  tags: string[];
  lideranca_id: string;
  base_legal: 'optin' | 'optout' | 'sem_base';
  optin_evidencia: EvidenciaOptin;
  optin_evidencia_ref: string;
};

const FORM_VAZIO: FormContato = {
  nome: '',
  telefone: '',
  email: '',
  bairro: '',
  nascimento: '',
  perfil: [],
  tags: [],
  lideranca_id: '',
  base_legal: 'optin',
  optin_evidencia: 'nao_informada',
  optin_evidencia_ref: '',
};

export function ContatosPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  // `bairroActiveId` é seleção de interface, não dado — segue local.
  const setBairroActiveId = useMockStore((s) => s.setBairroActiveId);
  const { bairros } = useBairros();
  // Rotulo da unidade territorial segue a escala do mandato (bairro, municipio, estado...)
  const { unidade, plural } = useEscala();

  const { data: liderancas = [] } = useQuery({
    queryKey: ['liderancas', {}],
    queryFn: () => liderancasApi.list({}, token),
    enabled: !!token,
  });

  const { data: categoriasContato = [] } = useQuery({
    queryKey: ['categorias', 'contato'],
    queryFn: () => categoriasApi.list(token, 'contato'),
    enabled: !!token,
  });

  // Categoria desativada some do seletor, mas continua legível no contato que
  // já a tem: apagar a etiqueta de quem foi marcado reescreveria o passado.
  const tagsDisponiveis = categoriasContato.filter((c) => c.ativa).map((c) => c.nome);

  const [q, setQ] = useState('');
  const [bairroFilter, setBairroFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [sort, setSort] = useState<'nome' | 'bairro' | 'recentes'>('nome');
  const [page, setPage] = useState(1);

  const filtros = {
    search: q || undefined,
    bairro: bairroFilter || undefined,
    tag: tagFilter || undefined,
    sort,
    page,
    limit: PAGE_SIZE,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['contatos', filtros],
    queryFn: () => contatosApi.list(filtros, token),
    enabled: !!token,
  });

  const contatos = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [selecionado, setSelecionado] = useState<Contato | null>(null);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalImport, setModalImport] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState<FormContato>(FORM_VAZIO);
  const [csv, setCsv] = useState('');

  /**
   * `?contato=<id>` abre a ficha daquela pessoa direto.
   *
   * É o outro lado do clique em "Solicitantes mais frequentes" nos painéis de
   * Solicitações (proto:1204, `openDrawer('contato', id)`): sem isso o clique
   * cairia na lista e o usuário procuraria a pessoa de novo na mão.
   * A lista é paginada, então quando o contato não está na página carregada
   * ele é buscado pelo id.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const contatoAlvo = searchParams.get('contato') ?? '';
  const naPagina = contatos.find((c) => c.id === contatoAlvo);

  const { data: contatoPorLink } = useQuery({
    queryKey: ['contatos', 'link', contatoAlvo],
    queryFn: () => contatosApi.get(contatoAlvo, token),
    enabled: !!token && !!contatoAlvo && !naPagina,
  });

  const contatoDoLink = naPagina ?? contatoPorLink;

  useEffect(() => {
    if (!contatoAlvo || !contatoDoLink) return;
    setSelecionado(contatoDoLink);
    const p = new URLSearchParams(searchParams);
    p.delete('contato');
    setSearchParams(p, { replace: true });
    // `searchParams`/`setSearchParams` fora das dependências de propósito:
    // o efeito precisa rodar na chegada do contato, não a cada troca de query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contatoAlvo, contatoDoLink]);

  /**
   * `?nova=1` abre o formulário de novo contato já aberto — mesma convenção do
   * `?nova=1` de Ações (`AcoesPage`), que é o que permite ao Resumo ter o botão
   * "Novo contato" do protótipo (proto:855, `openDrawer('novo-contato')`).
   *
   * O parâmetro é consumido e apagado da URL com `replace`: sem isso, recarregar
   * a página reabriria o formulário para sempre, e fechá-lo não teria efeito
   * nenhum na volta do histórico.
   */
  useEffect(() => {
    if (!searchParams.get('nova')) return;
    abrirNovo();
    const p = new URLSearchParams(searchParams);
    p.delete('nova');
    setSearchParams(p, { replace: true });
    // `abrirNovo` fora das dependências: ela é recriada a cada render e o efeito
    // só precisa rodar quando a query muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  // Mantém a ficha aberta sincronizada com a lista recarregada do servidor
  useEffect(() => {
    if (!selecionado) return;
    const atualizado = contatos.find((c) => c.id === selecionado.id);
    if (atualizado && atualizado.updated_at !== selecionado.updated_at) {
      setSelecionado(atualizado);
    }
  }, [contatos, selecionado]);

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['contatos'] });
    queryClient.invalidateQueries({ queryKey: ['aniversariantes'] });
  };

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const createMutation = useMutation({
    mutationFn: (payload: any) =>
      criarComFilaOffline(() => contatosApi.create(payload, token), {
        metodo: 'POST',
        url: '/gab/contatos',
        corpo: payload,
        rotulo: `Contato: ${payload.nome}`,
      }),
    onSuccess: (c) => {
      invalidar();
      toast(c ? 'Contato cadastrado com sucesso.' : 'Contato guardado para envio.');
      setModalNovo(false);
      setForm(FORM_VAZIO);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao cadastrar o contato.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      contatosApi.update(id, payload, token),
    onSuccess: (atualizado) => {
      invalidar();
      toast('Contato atualizado.');
      setSelecionado(atualizado);
      setEditando(false);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar o contato.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => contatosApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Contato removido da base.');
      setSelecionado(null);
      setEditando(false);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover o contato.')),
  });

  const importMutation = useMutation({
    mutationFn: (conteudo: string) => contatosApi.importCsv(conteudo, token),
    onSuccess: (res) => {
      invalidar();
      toast(`${res.count} contato(s) importado(s).`);
      setModalImport(false);
      setCsv('');
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao importar o CSV.')),
  });

  /** Monta o payload da API a partir do formulário, validando o que a API exige. */
  const montarPayload = (): any | null => {
    if (!form.nome.trim()) {
      toast('O nome é obrigatório.');
      return null;
    }

    let telefone: string | undefined;
    if (form.telefone.trim()) {
      const e164 = paraE164(form.telefone);
      if (!e164) {
        toast('Telefone inválido. Use DDD + número (ex: 85 98888-0000).');
        return null;
      }
      telefone = e164;
    }

    return {
      nome: form.nome.trim(),
      telefone_e164: telefone,
      email: form.email.trim() || undefined,
      bairro: form.bairro || undefined,
      municipio: form.bairro ? 'Fortaleza' : undefined,
      uf: form.bairro ? 'CE' : undefined,
      nascimento: form.nascimento || undefined,
      perfil: form.perfil,
      tags: form.tags,
      lideranca_id: form.lideranca_id || undefined,
      base_legal: form.base_legal,
      // So faz sentido enviar a prova quando ha consentimento a provar.
      optin_evidencia: form.base_legal === 'optin' ? form.optin_evidencia : undefined,
      optin_evidencia_ref:
        form.base_legal === 'optin' ? form.optin_evidencia_ref.trim() || undefined : undefined,
    };
  };

  const handleCriar = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = montarPayload();
    if (payload) createMutation.mutate(payload);
  };

  const handleSalvarEdicao = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selecionado) return;
    const payload = montarPayload();
    if (payload) updateMutation.mutate({ id: selecionado.id, payload });
  };

  const handleExcluir = () => {
    if (!selecionado) return;
    const ok = window.confirm(
      `Remover "${selecionado.nome}" da base de contatos?\n\nO registro é arquivado (exclusão reversível) e a ação fica na trilha de auditoria.`,
    );
    if (ok) deleteMutation.mutate(selecionado.id);
  };

  const abrirNovo = () => {
    setForm({ ...FORM_VAZIO, bairro: bairros[0]?.n || '' });
    setModalNovo(true);
  };

  const abrirEdicao = () => {
    if (!selecionado) return;
    setForm({
      nome: selecionado.nome,
      telefone: selecionado.telefone_e164 || '',
      email: selecionado.email || '',
      bairro: selecionado.bairro || '',
      nascimento: selecionado.nascimento ? selecionado.nascimento.slice(0, 10) : '',
      perfil: selecionado.perfil || [],
      tags: selecionado.tags || [],
      lideranca_id: selecionado.lideranca_id || '',
      base_legal: selecionado.base_legal,
      optin_evidencia: (selecionado as any).optin_evidencia ?? 'nao_informada',
      optin_evidencia_ref: (selecionado as any).optin_evidencia_ref ?? '',
    });
    setEditando(true);
  };

  const toggleTag = (tag: string) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(tag) ? f.tags.filter((t) => t !== tag) : [...f.tags, tag],
    }));
  };

  const togglePerfil = (perfil: string) => {
    setForm((f) => ({
      ...f,
      perfil: f.perfil.includes(perfil)
        ? f.perfil.filter((p) => p !== perfil)
        : [...f.perfil, perfil],
    }));
  };

  const abrirWhatsApp = (tel?: string, nome?: string) => {
    if (!tel) {
      toast('Este contato não tem telefone cadastrado.');
      return;
    }
    const num = tel.replace(/\D/g, '');
    const url = `https://wa.me/${num}?text=${encodeURIComponent(
      `Olá, ${nome}! Tudo bem? Aqui é do Gabinete da Vereadora Ana Silva.`,
    )}`;
    window.open(url, '_blank');
  };

  const liderancaDe = (c: Contato) => liderancas.find((l) => l.id === c.lideranca_id);

  const trocarFiltro = (fn: () => void) => {
    fn();
    setPage(1);
  };

  /** Campos compartilhados entre o cadastro e a edição. */
  const camposFormulario = (
    <>
      <div className="field">
        <label>Nome completo *</label>
        <input
          className="in"
          placeholder="Nome do apoiador ou cidadão"
          value={form.nome}
          onChange={(e) => setForm({ ...form, nome: e.target.value })}
          required
        />
      </div>

      <div className="grid g2">
        <div className="field">
          <label>Telefone / WhatsApp</label>
          <input
            className="in"
            placeholder="85 98888-0000"
            value={form.telefone}
            onChange={(e) => setForm({ ...form, telefone: e.target.value })}
          />
        </div>
        <div className="field">
          <label>E-mail</label>
          <input
            className="in"
            type="email"
            placeholder="contato@exemplo.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
      </div>

      <div className="grid g2">
        <div className="field">
          <label>{unidade} de residência</label>
          <select
            className="in"
            value={form.bairro}
            onChange={(e) => setForm({ ...form, bairro: e.target.value })}
          >
            <option value="">Não informado</option>
            {bairros.map((b) => (
              <option key={b.id} value={b.n}>
                {b.n}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Data de nascimento</label>
          <input
            className="in"
            type="date"
            value={form.nascimento}
            onChange={(e) => setForm({ ...form, nascimento: e.target.value })}
          />
        </div>
      </div>

      <div className="grid g2">
        <div className="field">
          <label>Vincular a liderança (opcional)</label>
          <select
            className="in"
            value={form.lideranca_id}
            onChange={(e) => setForm({ ...form, lideranca_id: e.target.value })}
          >
            <option value="">Nenhuma liderança vinculada</option>
            {liderancas.map((l) => (
              <option key={l.id} value={l.id}>
                {l.nome} ({l.tipo})
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Base legal (LGPD) *</label>
          <select
            className="in"
            value={form.base_legal}
            onChange={(e) => setForm({ ...form, base_legal: e.target.value as any })}
          >
            <option value="optin">Opt-in · consentiu receber mensagens</option>
            <option value="sem_base">Sem base · não pode receber campanha</option>
            <option value="optout">Opt-out · pediu descadastro</option>
          </select>
        </div>
      </div>

      {form.base_legal === 'optin' && (
        <div className="grid g2">
          <div className="field">
            <label>Como o consentimento foi obtido</label>
            <select
              className="in"
              value={form.optin_evidencia}
              onChange={(e) =>
                setForm({ ...form, optin_evidencia: e.target.value as EvidenciaOptin })
              }
            >
              {EVIDENCIAS_OPTIN.map((e) => (
                <option key={e.v} value={e.v}>
                  {e.l}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Onde está a prova</label>
            <input
              className="in"
              value={form.optin_evidencia_ref}
              onChange={(e) => setForm({ ...form, optin_evidencia_ref: e.target.value })}
              placeholder="Link, protocolo ou onde encontrar"
              maxLength={300}
            />
            <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4 }}>
              Numa fiscalização, é isto que demonstra o consentimento. Sem a prova, o
              registro afirma o opt-in sem poder sustentá-lo.
            </div>
          </div>
        </div>
      )}

      <div className="field">
        <label>Perfil da pessoa</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          {PERFIS_CONTATO.map((perfil) => {
            const on = form.perfil.includes(perfil);
            return (
              <span
                key={perfil}
                onClick={() => togglePerfil(perfil)}
                className={`chip chipbtn ${on ? 'acc on' : 'neutral'}`}
                style={{ cursor: 'pointer' }}
              >
                {perfil} {on ? '✓' : '+'}
              </span>
            );
          })}
        </div>
        <small>
          Mais de um é o caso comum: quem lidera uma associação também costuma ter comércio no
          bairro.
        </small>
      </div>

      <div className="field">
        <label>Áreas de interesse</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          {tagsDisponiveis.map((tag) => {
            const on = form.tags.includes(tag);
            return (
              <span
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`chip chipbtn ${on ? 'acc on' : 'neutral'}`}
                style={{ cursor: 'pointer' }}
              >
                {tag} {on ? '✓' : '+'}
              </span>
            );
          })}
          {tagsDisponiveis.length === 0 && (
            <small>
              Nenhuma área cadastrada. Elas são configuradas em Configurações &gt; Categorias.
            </small>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="page" id="p-contatos">
      {/* Cabeçalho */}
      <div className="head">
        <div>
          <h1>Contatos</h1>
          <p>
            Base do gabinete com consentimento explícito por pessoa. Nenhuma classificação de
            afinidade política: apenas lista de inclusão por ato do titular (LGPD art. 11).
          </p>
        </div>
        <div className="act">
          <button
            className="btn"
            onClick={() =>
              exportarCsv(
                'contatos',
                [
                  { cabecalho: 'Nome', valor: (c: Contato) => c.nome },
                  { cabecalho: 'Telefone', valor: (c: Contato) => c.telefone_e164 },
                  { cabecalho: 'E-mail', valor: (c: Contato) => c.email },
                  { cabecalho: unidade, valor: (c: Contato) => c.bairro },
                  { cabecalho: 'Perfil', valor: (c: Contato) => (c.perfil || []).join(' | ') },
                  { cabecalho: 'Áreas de interesse', valor: (c: Contato) => (c.tags || []).join(' | ') },
                  { cabecalho: 'Base legal', valor: (c: Contato) => c.base_legal },
                  {
                    cabecalho: 'Prova do consentimento',
                    valor: (c: any) => c.optin_evidencia ?? '',
                  },
                  {
                    cabecalho: 'Onde está a prova',
                    valor: (c: any) => c.optin_evidencia_ref ?? '',
                  },
                  { cabecalho: 'Nascimento', valor: (c: Contato) => c.nascimento?.slice(0, 10) ?? '' },
                ],
                contatos,
              )
            }
            disabled={contatos.length === 0}
            title="Exporta a página atual, com os filtros aplicados"
          >
            Exportar
          </button>
          <button className="btn" onClick={() => setModalImport(true)}>
            Importar CSV
          </button>
          <button className="btn pri" onClick={abrirNovo}>
            + Novo Contato
          </button>
        </div>
      </div>

      {/* Barra de Filtros */}
      <div className="filters">
        <input
          className="in"
          placeholder="Buscar por nome, telefone ou e-mail..."
          value={q}
          onChange={(e) => trocarFiltro(() => setQ(e.target.value))}
          style={{ width: 280 }}
        />

        <select
          className="sel"
          value={bairroFilter}
          onChange={(e) => trocarFiltro(() => setBairroFilter(e.target.value))}
        >
          <option value="">Todos os {plural}</option>
          {bairros.map((b) => (
            <option key={b.id} value={b.n}>
              {b.n}
            </option>
          ))}
        </select>

        <select
          className="sel"
          value={tagFilter}
          onChange={(e) => trocarFiltro(() => setTagFilter(e.target.value))}
        >
          <option value="">Todas as áreas de interesse</option>
          {tagsDisponiveis.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {/* Tabela de Contatos */}
      <div className="card flat tw">
        <table>
          <thead>
            <tr>
              <th
                className="sort"
                onClick={() => trocarFiltro(() => setSort('nome'))}
                style={{ cursor: 'pointer' }}
              >
                Nome {sort === 'nome' ? '↓' : ''}
              </th>
              <th>Telefone</th>
              <th
                className="sort"
                onClick={() => trocarFiltro(() => setSort('bairro'))}
                style={{ cursor: 'pointer' }}
              >
                {unidade} {sort === 'bairro' ? '↓' : ''}
              </th>
              <th>Perfis / Tags</th>
              <th>Liderança Vinculada</th>
              <th>Base legal</th>
              <th>Aniversário</th>
              <th style={{ textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {contatos.map((c) => {
              const lid = liderancaDe(c);
              return (
                <tr
                  key={c.id}
                  className="row"
                  onClick={() => {
                    setSelecionado(c);
                    setEditando(false);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <b>{c.nome}</b>
                  </td>
                  <td className="mono">{formatarTelefone(c.telefone_e164)}</td>
                  <td>{c.bairro || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {(c.tags || []).map((t) => (
                        <span key={t} className="chip neutral">
                          {t}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    {lid ? (
                      <span className="chip acc">{lid.nome}</span>
                    ) : (
                      <span style={{ color: 'var(--ink3)', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`chip ${
                        c.base_legal === 'optin' ? 'good' : c.base_legal === 'optout' ? 'crit' : 'neutral'
                      }`}
                    >
                      {ROTULO_BASE_LEGAL[c.base_legal] || c.base_legal}
                    </span>
                  </td>
                  <td>{formatarDiaMes(c.nascimento)}</td>
                  <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    {podeEnviarWhatsApp(c) ? (
                      <button
                        className="btn sm"
                        title="Abrir WhatsApp para conversa manual"
                        onClick={() => abrirWhatsApp(c.telefone_e164, c.nome)}
                      >
                        💬 WhatsApp
                      </button>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{motivoBloqueio(c)}</span>
                    )}
                  </td>
                </tr>
              );
            })}

            {isLoading && (
              <tr>
                <td colSpan={8} className="empty">
                  Carregando contatos...
                </td>
              </tr>
            )}
            {isError && !isLoading && (
              <tr>
                <td colSpan={8} className="empty">
                  Não foi possível carregar os contatos: {erroDe(error, 'erro de comunicação com a API.')}
                </td>
              </tr>
            )}
            {!isLoading && !isError && contatos.length === 0 && (
              <tr>
                <td colSpan={8} className="empty">
                  Nenhum contato encontrado com os filtros aplicados.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Paginação (servidor) */}
        <div className="pager">
          <span>{total.toLocaleString('pt-BR')} contatos</span>
          <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => Math.abs(p - page) <= 2 || p === 1 || p === totalPages)
            .map((p) => (
              <button key={p} className={page === p ? 'on' : ''} onClick={() => setPage(p)}>
                {p}
              </button>
            ))}
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            ›
          </button>
        </div>
      </div>

      {/* Drawer: Ficha 360 do Contato */}
      <Drawer
        aberto={!!selecionado}
        titulo={editando ? 'Editar contato' : selecionado?.nome || ''}
        aoFechar={() => {
          setSelecionado(null);
          setEditando(false);
        }}
      >
        {selecionado && (
          <>
            <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: -8, marginBottom: 4 }}>
              {selecionado.bairro || `${unidade} não informado`}
            </div>

            {editando ? (
              <form onSubmit={handleSalvarEdicao} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {camposFormulario}

                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" className="btn" onClick={() => setEditando(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn pri" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{ color: 'var(--crit)', marginLeft: 'auto' }}
                    disabled={deleteMutation.isPending}
                    onClick={handleExcluir}
                  >
                    Excluir
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="filters" style={{ marginBottom: 16 }}>
                  {(selecionado.perfil || []).map((p) => (
                    <span key={p} className="chip warn">
                      {p}
                    </span>
                  ))}
                  {(selecionado.tags || []).map((t) => (
                    <span key={t} className="chip neutral">
                      {t}
                    </span>
                  ))}
                  {liderancaDe(selecionado) && (
                    <span className="chip acc">Liderança: {liderancaDe(selecionado)?.nome}</span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                  {podeEnviarWhatsApp(selecionado) ? (
                    <button
                      className="btn pri sm"
                      onClick={() => abrirWhatsApp(selecionado.telefone_e164, selecionado.nome)}
                    >
                      💬 Abrir WhatsApp
                    </button>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--ink3)', alignSelf: 'center' }}>
                      {motivoBloqueio(selecionado)}
                    </span>
                  )}
                  {selecionado.telefone_e164 && (
                    <a
                      className="btn sm"
                      href={`tel:${selecionado.telefone_e164}`}
                      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                    >
                      📞 Ligar
                    </a>
                  )}
                  <button className="btn sm" onClick={() => navigate('/solicitacoes')}>
                    + Solicitação
                  </button>
                  <button className="btn sm" onClick={abrirEdicao}>
                    ✏️ Editar
                  </button>
                </div>

                <dl className="dl" style={{ marginBottom: 20 }}>
                  <dt>Telefone</dt>
                  <dd className="mono">{formatarTelefone(selecionado.telefone_e164)}</dd>
                  <dt>E-mail</dt>
                  <dd>{selecionado.email || '—'}</dd>
                  <dt>{unidade}</dt>
                  <dd>
                    {selecionado.bairro ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const b = bairros.find((x) => x.n === selecionado.bairro);
                          if (b) setBairroActiveId(b.id);
                          navigate('/territorio');
                        }}
                        style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                      >
                        {selecionado.bairro}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                  <dt>Aniversário</dt>
                  <dd>{formatarDiaMes(selecionado.nascimento)}</dd>
                  <dt>Origem do cadastro</dt>
                  <dd>{selecionado.origem || 'Cadastro manual'}</dd>
                </dl>

                {/*
                  proto:911 (a versão de `DRAWERS.contato` que vence em
                  runtime) tem um card dedicado "Consentimento (LGPD art.
                  11)", separado da lista de dados cadastrais. "Base legal" e
                  "Prova do consentimento" saíram do <dl> acima e entraram
                  aqui, junto com a data e a mensagem de opt-out/sem-base que
                  o protótipo mostra e o <dl> não tinha.
                */}
                <CardConsentimento contato={selecionado} />

                {/* O item de LGPD guardava so o TIPO da evidencia e uma
                    referencia textual, porque nao havia onde por arquivo.
                    Com os anexos, o audio e o print passam a ficar junto do
                    contato que eles provam. */}
                <Anexos
                  donoTipo="contato"
                  donoId={selecionado.id}
                  tipoPadrao="Prova de consentimento"
                  titulo="Provas e documentos"
                  ajuda="O áudio, o print da conversa ou o formulário assinado que sustentam o consentimento."
                />

                <VisitasDoContato contatoId={selecionado.id} />

                {/* Histórico com base nos carimbos reais do registro */}
                <h3>Histórico do Cadastro</h3>
                <p style={{ fontSize: 11, color: 'var(--ink3)', margin: '4px 0 12px' }}>
                  Eventos registrados na base. Cada alteração também é gravada na trilha de auditoria.
                </p>
                <div className="tl">
                  <div className="tli">
                    <div className="d">{formatarData(selecionado.created_at)}</div>
                    <div className="t">
                      <b>Cadastro criado</b>
                    </div>
                    <div className="desc">Registro inserido na base de contatos do gabinete.</div>
                  </div>
                  {selecionado.optin_ts && (
                    <div className="tli">
                      <div className="d">{formatarData(selecionado.optin_ts)}</div>
                      <div className="t">
                        <b>Consentimento registrado (opt-in)</b>
                      </div>
                      <div className="desc">Autorizado a receber comunicações do mandato.</div>
                    </div>
                  )}
                  {selecionado.optout_ts && (
                    <div className="tli">
                      <div className="d">{formatarData(selecionado.optout_ts)}</div>
                      <div className="t">
                        <b>Descadastro solicitado (opt-out)</b>
                      </div>
                      <div className="desc">Excluído de campanhas e réguas automáticas.</div>
                    </div>
                  )}
                  {selecionado.updated_at !== selecionado.created_at && (
                    <div className="tli">
                      <div className="d">{formatarData(selecionado.updated_at)}</div>
                      <div className="t">
                        <b>Cadastro atualizado</b>
                      </div>
                      <div className="desc">Última alteração dos dados cadastrais.</div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </Drawer>

      {/* Modal: Novo Contato */}
      {modalNovo && (
        <form onSubmit={handleCriar}>
          <Drawer
            aberto
            titulo="Cadastrar Novo Contato"
            aoFechar={() => setModalNovo(false)}
            rodape={
              <>
                <button type="button" className="btn" onClick={() => setModalNovo(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn pri" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Salvando...' : 'Salvar Contato'}
                </button>
              </>
            }
          >
            {camposFormulario}
          </Drawer>
        </form>
      )}

      {/* Modal: Importação CSV */}
      {modalImport && (
        <Drawer
          aberto
          titulo="Importar contatos por CSV"
          aoFechar={() => setModalImport(false)}
          rodape={
            <>
              <button type="button" className="btn" onClick={() => setModalImport(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn pri"
                disabled={importMutation.isPending || !csv.trim()}
                onClick={() => importMutation.mutate(csv)}
              >
                {importMutation.isPending ? 'Importando...' : 'Importar'}
              </button>
            </>
          }
        >
          {/*
            O formato fica visível porque é o que a pessoa precisa digitar; o
            detalhe de cada coluna foi para o ícone.
          */}
          <div className="note" style={{ fontSize: 12, marginBottom: 12 }}>
            Uma linha por contato, na ordem:{' '}
            <b className="mono">nome,telefone,email,bairro,base_legal,tags</b>{' '}
            <Info titulo="Detalhe das colunas do CSV">
              As tags são separadas por ponto e vírgula. A base legal é obrigatória e aceita{' '}
              <span className="mono">optin</span>, <span className="mono">optout</span> ou{' '}
              <span className="mono">sem_base</span>. A importação inteira é cancelada se alguma
              linha estiver irregular.
            </Info>
          </div>

          <div className="field">
            <label>Conteúdo do CSV (sem cabeçalho)</label>
            <textarea
              className="in"
              rows={8}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
              placeholder={'Maria Souza,85988880000,maria@ex.com,Messejana,optin,Saúde;Creche'}
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
            />
          </div>
        </Drawer>
      )}
    </div>
  );
}

/**
 * "Visitas deste contato", na ficha.
 *
 * O protótipo promete, no card "Converter em ação" da agenda interna, que "a
 * visita entra na linha do tempo de cada participante" (proto:1303). A linha
 * do tempo genérica do contato não existe neste sistema — a única que existe é
 * a da solicitação. O que a promessa vale de fato, e o que esta seção entrega,
 * é a pergunta inversa da lista de presença: em que visitas esta pessoa
 * esteve. Nenhum número é fabricado para respondê-la.
 */
function VisitasDoContato({ contatoId }: { contatoId: string }) {
  const token = useAuthStore((s) => s.token) || '';

  const { data: visitas = [], isLoading } = useQuery({
    queryKey: ['visitas-do-contato', contatoId],
    queryFn: () => visitasApi.listarPorContato(contatoId, token),
    enabled: !!token && !!contatoId,
  });

  return (
    <div data-testid="visitas-do-contato" style={{ marginTop: 16 }}>
      <h3>
        Visitas deste contato{' '}
        {visitas.length > 0 && <span className="chip neutral">{visitas.length}</span>}
      </h3>
      <p style={{ fontSize: 11, color: 'var(--ink3)', margin: '4px 0 12px' }}>
        Agendas internas em que a presença desta pessoa foi registrada.
      </p>

      {isLoading && <div className="empty">Carregando visitas...</div>}

      {/* Seção vazia, e não seção ausente: as duas dizem coisas diferentes. */}
      {!isLoading && visitas.length === 0 && (
        <div className="empty" style={{ fontSize: 12 }}>
          Nenhuma visita registrada com esta pessoa.
        </div>
      )}

      <div className="tl">
        {visitas.map((v) => (
          <div className="tli" key={v.id}>
            <div className="d">{formatarData(v.data_visita)}</div>
            <div className="t">
              <b>{v.bairro || 'Território não informado'}</b>{' '}
              <span className={`chip ${v.status === 'concluida' ? 'good' : 'neutral'}`}>
                {v.status === 'concluida'
                  ? 'Concluída'
                  : v.status === 'cancelada'
                    ? 'Cancelada'
                    : 'Planejada'}
              </span>
            </div>
            {v.relatorio_campo && <div className="desc">{v.relatorio_campo}</div>}
            {v.proximos_passos && (
              <div className="desc" style={{ color: 'var(--ink3)' }}>
                {v.proximos_passos}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
